import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

import ErrorBoundary from './components/ErrorBoundary';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ReferralPage from './pages/ReferralPage';
import PublicWorkerProfile from './pages/PublicWorkerProfile';
import WorkerDashboard from './pages/WorkerDashboard';
import EmployerDashboard from './pages/EmployerDashboard';
import AdminDashboard from './pages/AdminDashboard';
import SettingsPage from './pages/SettingsPage';
import CertificationsPage from './pages/CertificationsPage';
import ABNVerificationPage from './pages/ABNVerificationPage';
import IdentityVerificationPage from './pages/IdentityVerificationPage';
import CompliancePage from './pages/CompliancePage';
import NotFoundPage from './pages/NotFoundPage';
import ReportUserPage from './pages/ReportUserPage';
import ReviewsPage from './pages/ReviewsPage';
import TrainingPage from './pages/TrainingPage';
import PricingPage from './pages/PricingPage';
import CityPage from './pages/CityPage';
import InsurancePage from './pages/InsurancePage';
import MigrantHubPage from './pages/MigrantHubPage';
import VisaAgentPage from './pages/VisaAgentPage';
import AbilityHubPage from './pages/AbilityHubPage';
import ClientPortalPage from './pages/ClientPortalPage';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (storedUser && token) {
      try { setUser(JSON.parse(storedUser)); }
      catch { localStorage.removeItem('user'); localStorage.removeItem('token'); }
    }
    setLoading(false);
  }, []);

  const handleLogin = (loginData) => {
    setUser(loginData.user);
    localStorage.setItem('token', loginData.token);
    localStorage.setItem('user', JSON.stringify(loginData.user));
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-neutral-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-neutral-500 text-sm">Loading ShiftMatch…</span>
        </div>
      </div>
    );
  }

  const roleHome = user ? `/${user.role}` : '/login';

  return (
    <ErrorBoundary>
    <HelmetProvider>
    <Router>
      <Routes>
        {/* Public */}
        <Route path="/" element={user ? <Navigate to={roleHome} /> : <LandingPage />} />
        <Route path="/login" element={user ? <Navigate to={roleHome} /> : <LoginPage onLogin={handleLogin} />} />
        <Route path="/register" element={user ? <Navigate to={roleHome} /> : <RegisterPage onRegister={handleLogin} />} />

        {/* Protected — each dashboard owns its own nav/header */}
        <Route path="/worker/*" element={
          user?.role === 'worker'
            ? <WorkerDashboard user={user} onLogout={handleLogout} />
            : <Navigate to="/login" />
        } />
        <Route path="/employer/*" element={
          user?.role === 'employer'
            ? <EmployerDashboard user={user} onLogout={handleLogout} />
            : <Navigate to="/login" />
        } />
        <Route path="/admin/*" element={
          user?.role === 'admin'
            ? <AdminDashboard user={user} onLogout={handleLogout} />
            : <Navigate to="/login" />
        } />

        {/* Settings */}
        <Route path="/settings" element={user ? <SettingsPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
        <Route path="/referrals" element={user ? <ReferralPage user={user} /> : <Navigate to="/login" />} />
        <Route path="/worker/:userId/public" element={<PublicWorkerProfile />} />
        <Route path="/certifications" element={user?.role === 'worker' ? <CertificationsPage user={user} /> : <Navigate to="/login" />} />
        <Route path="/abn-verification" element={user?.role === 'employer' ? <ABNVerificationPage user={user} /> : <Navigate to="/login" />} />
        <Route path="/identity-verification" element={user?.role === 'worker' ? <IdentityVerificationPage user={user} /> : <Navigate to="/login" />} />
        <Route path="/compliance" element={user ? <CompliancePage user={user} /> : <Navigate to="/login" />} />
        <Route path="/report" element={user ? <ReportUserPage /> : <Navigate to="/login" />} />
        <Route path="/reviews/:userId" element={<ReviewsPage />} />
        <Route path="/training" element={<TrainingPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/shifts/:slug" element={<CityPage />} />
        <Route path="/insurance" element={<InsurancePage />} />
        <Route path="/migrant-hub" element={<MigrantHubPage />} />
        <Route path="/visa-agent" element={<VisaAgentPage />} />
        <Route path="/ability-hub" element={<AbilityHubPage />} />
        <Route path="/portal/:token" element={<ClientPortalPage />} />

        {/* Catch-all */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Router>
    </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
